// ActivepatientApi.js
// Centralized API client for Active Patients, Medical Records, and Prescriptions.
// Uses the shared axios instance (auto Keycloak auth via interceptors).
import api from "./axios";

const PROVIDERS_PATH = "/api/v1/providers";
const MEDICAL_RECORDS_PATH = "/api/v1/medical-records";
const PRESCRIPTIONS_PATH = "/api/v1/prescriptions";
const PATIENTS_PATH = "/api/v1/patients";
const MEDICATIONS_PATH = "/api/v1/medications";

/* ============================
 * Active Patients (Cards + Detail)
 * ============================ */

/**
 * List Active Patients (cards) for a provider (paged).
 * GET /api/providers/{providerId}/active-patients?page=&size=
 */
export const getActivePatients = async (
  providerId,
  { page = 0, size = 20 } = {},
) => {
  const res = await api.get(`${PROVIDERS_PATH}/${providerId}/active-patients`, {
    params: { page, size },
  });
  return res.data;
};

/**
 * Aggregated detail: Patient Profile + Medical Records + Prescriptions.
 * GET /api/providers/{providerId}/active-patients/{patientId}
 */
export const getActivePatientDetail = async (providerId, patientId) => {
  const res = await api.get(
    `${PROVIDERS_PATH}/${providerId}/active-patients/${patientId}`,
  );
  return res.data;
};

export const getActivePatientInsuranceDocuments = async (
  providerId,
  patientId,
) => {
  const res = await api.get(
    `${PROVIDERS_PATH}/${providerId}/active-patients/${patientId}/insurance-documents`,
  );
  return res.data;
};

export const viewDocumentInline = async (documentId) => {
  const res = await api.get(`/api/v1/documents/${documentId}/view`, {
    responseType: "blob",
  });
  return URL.createObjectURL(res.data);
};

/* ============================
 * Patient (direct) – optional helper
 * ============================ */

/**
 * Direct patient fetch (if you need it separately).
 * GET /api/patients/{patientId}
 */
export const getPatientProfile = async (patientId) => {
  const res = await api.get(`${PATIENTS_PATH}/${patientId}`);
  return res.data;
};

/* ============================
 * Medical Records CRUD
 * ============================ */

/**
 * List medical records for a patient (paged/filtered).
 * GET /api/medical-records?patientId=&page=&size=&q=&from=&to=
 */
export const listMedicalRecords = async (
  patientId,
  { page = 0, size = 20, q, from, to } = {},
) => {
  const res = await api.get(MEDICAL_RECORDS_PATH, {
    params: { patientId, page, size, q, from, to },
  });
  return res.data;
};

/**
 * Get a single medical record by ID.
 * GET /api/medical-records/{recordId}
 */
export const getMedicalRecord = async (recordId) => {
  const res = await api.get(`${MEDICAL_RECORDS_PATH}/${recordId}`);
  return res.data;
};

/**
 * Create a medical record.
 * POST /api/medical-records
 * body should include at least { patientId, ...fields }
 */
export const createMedicalRecord = async (payload) => {
  const res = await api.post(MEDICAL_RECORDS_PATH, payload);
  return res.data;
};

/**
 * Update a medical record.
 * PUT /api/medical-records/{recordId}
 */
export const updateMedicalRecord = async (recordId, payload) => {
  const res = await api.put(`${MEDICAL_RECORDS_PATH}/${recordId}`, payload);
  return res.data;
};

/**
 * Delete a medical record.
 * DELETE /api/medical-records/{recordId}
 */
export const deleteMedicalRecord = async (recordId) => {
  const res = await api.delete(`${MEDICAL_RECORDS_PATH}/${recordId}`);
  return res.data;
};

/* ============================
 * Prescriptions CRUD
 * ============================ */

/**
 * List prescriptions for a patient (paged/filtered).
 * GET /api/prescriptions?patientId=&page=&size=&status=&q=
 */
export const listPrescriptions = async (
  patientId,
  { page = 0, size = 20, status, q } = {},
) => {
  const res = await api.get(PRESCRIPTIONS_PATH, {
    params: { patientId, page, size, status, q },
  });
  return res.data;
};

/**
 * Get a prescription by ID.
 * GET /api/prescriptions/{prescriptionId}
 */
export const getPrescription = async (prescriptionId) => {
  const res = await api.get(`${PRESCRIPTIONS_PATH}/${prescriptionId}`);
  return res.data;
};

/**
 * Create a prescription.
 * POST /api/prescriptions
 * body should include at least { patientId, ...fields }
 */
export const createPrescription = async (payload) => {
  const res = await api.post(PRESCRIPTIONS_PATH, payload);
  return res.data;
};

/**
 * Update a prescription.
 * PUT /api/prescriptions/{prescriptionId}
 */
export const updatePrescription = async (prescriptionId, payload) => {
  const res = await api.put(`${PRESCRIPTIONS_PATH}/${prescriptionId}`, payload);
  return res.data;
};

/**
 * Delete a prescription.
 * DELETE /api/prescriptions/{prescriptionId}
 */
export const deletePrescription = async (prescriptionId) => {
  const res = await api.delete(`${PRESCRIPTIONS_PATH}/${prescriptionId}`);
  return res.data;
};

/* ============================
 * Medications
 * ============================ */

/**
 * List all available medications.
 * GET /api/medications
 */
export const getAllMedications = async () => {
  const res = await api.get(MEDICATIONS_PATH);
  return res.data;
};

/* ============================
 * Convenience export
 * ============================ */

const ActivepatientApi = {
  // Active Patients
  getActivePatients,
  getActivePatientDetail,
  getActivePatientInsuranceDocuments,
  viewDocumentInline,

  // Patient (direct)
  getPatientProfile,

  // Medical Records
  listMedicalRecords,
  getMedicalRecord,
  createMedicalRecord,
  updateMedicalRecord,
  deleteMedicalRecord,

  // Prescriptions
  listPrescriptions,
  getPrescription,
  createPrescription,
  updatePrescription,
  deletePrescription,

  // Medications
  getAllMedications,
};

export default ActivepatientApi;
